import { PageClassSelector, useTeacherWorkspace } from '../../../components/layout/TeacherWorkspace';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import TeacherNav from '../../../components/TeacherNav';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { assignmentsApi } from '../../../services/apiV2';
import { savedTeacherSection, rememberTeacherSection } from '../../../services/teacherSection';

interface QueueAssignmentRow {
  assignment_id: number;
  run_id: number | null;
  run_is_test_mode?: boolean;
  lesson_plan_id: number;
  lesson_plan_title: string;
  section_id: number;
  section_name: string;
  assigned_at: string;
  due_at: string | null;
  delivery_mode: string;
  purpose?: 'makeup' | 'assessment' | 'practice' | string;
  assessment_stage?: string;
  student_status: 'not_started' | 'in_progress' | 'completed' | 'excused' | string;
  makeup_review?: { status: 'pending' | 'needs_revision' | 'approved'; note: string } | null;
  why_in_queue?: string;
  target_scope?: 'all' | 'custom' | 'group' | string;
  target_group_name?: string;
  target_student_count?: number;
  target_summary?: string;
  triage?: {
    statuses: string[];
    objective_correct_count: number;
    objective_answered_count: number;
    objective_item_count: number;
    objective_score_percent: number | null;
    written_response_count: number;
    written_reviewed_count: number;
    written_pending_count: number;
    written_needs_revision_count: number;
    ai_analyzed_count: number;
    follow_up_recommended_count: number;
    required_artifact_count: number;
    missing_artifact_count: number;
    required_observation_count: number;
    pending_observation_count: number;
    teacher_verified: boolean;
    headrush_upload_confirmed: boolean;
    headrush_confirmed_at: string | null;
    retake_requested: boolean;
  } | null;
}

interface QueueStudentRow {
  student_id: number;
  student_name: string;
  assignment_count: number;
  assignments: QueueAssignmentRow[];
}

interface AssessmentRosterGroup {
  assignment: QueueAssignmentRow;
  students: Array<{ student: QueueStudentRow; assignment: QueueAssignmentRow }>;
}

type SectionVisibility = 'current' | 'archived' | 'all';
type QueuePurpose = 'makeup' | 'assessment' | 'practice';
type MakeupFilter = 'action' | 'all' | 'reviewed';
type TriageFilter = 'all' | 'needs_written_review' | 'missing_evidence' | 'follow_up_recommended'
  | 'retake_requested' | 'ready_for_teacher_verification' | 'ready_for_headrush_upload' | 'headrush_upload_confirmed';

const triageLabels: Record<string, string> = {
  retake_requested: 'Retake approval requested',
  needs_written_review: 'Needs written review',
  missing_evidence: 'Missing required evidence',
  follow_up_recommended: 'Follow-up recommended',
  ready_for_teacher_verification: 'Ready for teacher verification',
  ready_for_headrush_upload: 'Ready for Headrush upload',
  headrush_upload_confirmed: 'Headrush upload confirmed',
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return 'No date set';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Invalid date';
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const statusPillClass = (status?: string): string => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'completed') return 'bg-emerald-100 text-emerald-700';
  if (normalized === 'in_progress') return 'bg-amber-100 text-amber-700';
  if (normalized === 'not_started') return 'bg-slate-100 text-slate-700';
  return 'bg-gray-100 text-gray-700';
};

const formatStatusLabel = (status?: string): string => (
  String(status || 'not_started')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
);

const supportsTeacherReview = (deliveryMode?: string): boolean => {
  const normalized = String(deliveryMode || '').trim().toLowerCase();
  return normalized === 'replay_lesson' || normalized === 'replay_exam';
};

const readEnumParam = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T => (
  value && allowed.includes(value as T) ? value as T : fallback
);

const TeacherQueueContent: React.FC = () => {
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_queue');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const restoredScrollRef = useRef(false);
  const [students, setStudents] = useState<QueueStudentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const workspace = useTeacherWorkspace();
  const [sectionVisibility, setSectionVisibility] = useState<SectionVisibility>(() => readEnumParam(
    searchParams.get('visibility'), ['current', 'archived', 'all'] as const, workspace?.sections.find(row => String(row.id) === workspace.selected)?.is_active === false ? 'archived' : 'current',
  ));
  const [queuePurpose, setQueuePurpose] = useState<QueuePurpose>(() => readEnumParam(
    searchParams.get('purpose'), ['makeup', 'assessment', 'practice'] as const, 'makeup',
  ));
  const [triageFilter, setTriageFilter] = useState<TriageFilter>(() => readEnumParam(
    searchParams.get('triage'), [
      'all', 'needs_written_review', 'missing_evidence', 'follow_up_recommended',
      'retake_requested', 'ready_for_teacher_verification', 'ready_for_headrush_upload',
      'headrush_upload_confirmed',
    ] as const, 'all',
  ));
  const [makeupFilter, setMakeupFilter] = useState<MakeupFilter>(() => readEnumParam(
    searchParams.get('makeup_status'), ['action', 'all', 'reviewed'] as const, 'action',
  ));
  const [selectedSectionId, setSelectedSectionId] = useState<string>(() => searchParams.get('section') || searchParams.get('section_id') || savedTeacherSection());
  useEffect(() => { rememberTeacherSection(selectedSectionId); }, [selectedSectionId]);
  const [studentQuery, setStudentQuery] = useState<string>(() => searchParams.get('q') || '');
  const [removingStudentIds, setRemovingStudentIds] = useState<Set<number>>(new Set());
  const [removingAssignmentKeys, setRemovingAssignmentKeys] = useState<Set<string>>(new Set());
  const [reviewingAssignmentKeys, setReviewingAssignmentKeys] = useState<Set<string>>(new Set());

  const assignmentActionKey = useCallback((studentId: number, assignmentId: number): string => (
    `${studentId}:${assignmentId}`
  ), []);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await assignmentsApi.teacherQueue(sectionVisibility, queuePurpose);
      const rows = Array.isArray(payload?.students) ? payload.students : [];
      setStudents(rows);
    } catch (err: any) {
      setError(err?.response?.data?.error || `Failed to load the ${queuePurpose === 'assessment' ? 'Assessment Triage' : queuePurpose === 'practice' ? 'Unit Practice Queue' : 'Make-up Queue'}.`);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [queuePurpose, sectionVisibility]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set('purpose', queuePurpose);
    if (sectionVisibility !== 'current') next.set('visibility', sectionVisibility);
    next.set('section', selectedSectionId);
    if (queuePurpose === 'assessment' && triageFilter !== 'all') next.set('triage', triageFilter);
    if (queuePurpose !== 'assessment' && makeupFilter !== 'action') next.set('makeup_status', makeupFilter);
    if (studentQuery.trim()) next.set('q', studentQuery.trim());
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [queuePurpose, searchParams, sectionVisibility, selectedSectionId, setSearchParams, studentQuery, triageFilter, makeupFilter]);

  useEffect(() => {
    if (loading || students.length === 0 || restoredScrollRef.current) return;
    const restoreScrollY = Number((location.state as any)?.restoreScrollY);
    if (!Number.isFinite(restoreScrollY) || restoreScrollY <= 0) return;
    restoredScrollRef.current = true;
    window.scrollTo({ top: restoreScrollY, behavior: 'auto' });
  }, [loading, location.state, students]);

  const sectionOptions = useMemo(() => {
    const optionsById = new Map<number, { id: number; name: string }>();
    students.forEach((student) => {
      (student.assignments || []).forEach((assignment) => {
        if (!optionsById.has(assignment.section_id)) {
          optionsById.set(assignment.section_id, {
            id: assignment.section_id,
            name: assignment.section_name || `Section #${assignment.section_id}`,
          });
        }
      });
    });
    return Array.from(optionsById.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [students]);

  const filteredStudents = useMemo(() => {
    const normalizedQuery = studentQuery.trim().toLowerCase();
    return students
      .map((student) => {
        const matchingAssignments = (student.assignments || []).filter((assignment) => (
          (selectedSectionId === 'all' || String(assignment.section_id) === selectedSectionId)
          && (queuePurpose !== 'assessment'
            || triageFilter === 'all'
            || (assignment.triage?.statuses || []).includes(triageFilter))
          && (queuePurpose === 'assessment' || makeupFilter === 'all'
            || (makeupFilter === 'reviewed'
              ? assignment.makeup_review?.status === 'approved'
              : assignment.makeup_review?.status !== 'approved'))
        ));
        return {
          ...student,
          assignments: matchingAssignments,
          assignment_count: matchingAssignments.length,
          total_assignment_count: Array.isArray(student.assignments) ? student.assignments.length : 0,
        };
      })
      .filter((student) => student.assignments.length > 0)
      .filter((student) => (
        normalizedQuery.length === 0
          ? true
          : String(student.student_name || '').toLowerCase().includes(normalizedQuery)
      ));
  }, [queuePurpose, selectedSectionId, studentQuery, students, triageFilter, makeupFilter]);

  const handleMakeupReview = useCallback(async (student: QueueStudentRow, assignment: QueueAssignmentRow, decision: 'approved' | 'needs_revision') => {
    const note = decision === 'needs_revision'
      ? window.prompt(`What should ${student.student_name} revise? A new attempt will open for them.`)
      : '';
    if (decision === 'needs_revision' && !note?.trim()) return;
    const key = assignmentActionKey(student.student_id, assignment.assignment_id);
    setReviewingAssignmentKeys((prev) => new Set(prev).add(key));
    setError(null);
    try {
      await assignmentsApi.reviewMakeup(assignment.assignment_id, student.student_id, { decision, note: note?.trim() });
      setNotice(decision === 'approved'
        ? `${student.student_name}'s make-up was reviewed and moved to Reviewed.`
        : `${student.student_name} can now revise and resubmit this make-up.`);
      await loadQueue();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save the make-up review.');
    } finally {
      setReviewingAssignmentKeys((prev) => { const next = new Set(prev); next.delete(key); return next; });
    }
  }, [assignmentActionKey, loadQueue]);

  const assessmentCounts = useMemo(() => {
    const rows = filteredStudents.flatMap((student) => student.assignments || []);
    return {
      written: rows.filter((row) => row.triage?.statuses.includes('needs_written_review')).length,
      missing: rows.filter((row) => row.triage?.statuses.includes('missing_evidence')).length,
      verify: rows.filter((row) => row.triage?.statuses.includes('ready_for_teacher_verification')).length,
      upload: rows.filter((row) => row.triage?.statuses.includes('ready_for_headrush_upload')).length,
    };
  }, [filteredStudents]);

  const visibleAssessmentSummaries = useMemo(() => {
    const byAssignmentId = new Map<number, QueueAssignmentRow>();
    filteredStudents.forEach((student) => {
      student.assignments.forEach((assignment) => {
        if (!byAssignmentId.has(assignment.assignment_id)) byAssignmentId.set(assignment.assignment_id, assignment);
      });
    });
    return Array.from(byAssignmentId.values()).sort((a, b) => (
      a.section_name.localeCompare(b.section_name)
      || a.lesson_plan_title.localeCompare(b.lesson_plan_title)
      || a.assignment_id - b.assignment_id
    ));
  }, [filteredStudents]);

  const assessmentRosterGroups = useMemo<AssessmentRosterGroup[]>(() => {
    const groups = new Map<number, AssessmentRosterGroup>();
    filteredStudents.forEach((student) => {
      student.assignments.forEach((assignment) => {
        const existing = groups.get(assignment.assignment_id) || { assignment, students: [] };
        existing.students.push({ student, assignment });
        groups.set(assignment.assignment_id, existing);
      });
    });
    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        students: group.students.sort((a, b) => a.student.student_name.localeCompare(b.student.student_name)),
      }))
      .sort((a, b) => (
        a.assignment.section_name.localeCompare(b.assignment.section_name)
        || a.assignment.lesson_plan_title.localeCompare(b.assignment.lesson_plan_title)
        || a.assignment.assignment_id - b.assignment.assignment_id
      ));
  }, [filteredStudents]);

  const openStudentReview = useCallback((studentId: number, assignmentId: number) => {
    navigate(
      `/v2/teacher/assignments/${assignmentId}/students/${studentId}`,
      {
        state: {
          queueReturn: `${location.pathname}${location.search}`,
          queueScrollY: window.scrollY,
        },
      },
    );
  }, [location.pathname, location.search, navigate]);

  const totalAssignments = useMemo(() => (
    students.reduce((count, student) => count + (Array.isArray(student.assignments) ? student.assignments.length : 0), 0)
  ), [students]);

  const filteredAssignmentsCount = useMemo(() => (
    filteredStudents.reduce((count, student) => count + student.assignments.length, 0)
  ), [filteredStudents]);

  const handleRemoveStudentFromAll = useCallback(async (
    student: QueueStudentRow,
    assignmentRows: QueueAssignmentRow[],
  ) => {
    if (assignmentRows.length === 0) return;

    const assignmentLabel = assignmentRows.length === 1 ? 'assignment' : 'assignments';
    const confirmed = window.confirm(
      `Remove ${student.student_name} from ${assignmentRows.length} active make-up ${assignmentLabel}?`,
    );
    if (!confirmed) return;

    setRemovingStudentIds((prev) => new Set([...Array.from(prev), student.student_id]));
    setError(null);
    setNotice(null);

    try {
      await Promise.all(
        assignmentRows.map((assignment) => (
          assignmentsApi.excuseStudent(assignment.assignment_id, student.student_id, {
            excused: true,
            reason: 'Removed by teacher from consolidated make-up queue.',
          })
        )),
      );
      setNotice(`Removed ${student.student_name} from ${assignmentRows.length} active make-up ${assignmentLabel}.`);
      await loadQueue();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to remove student from all active assignments.');
    } finally {
      setRemovingStudentIds((prev) => {
        const next = new Set(prev);
        next.delete(student.student_id);
        return next;
      });
    }
  }, [loadQueue]);

  const handleRemoveStudentFromAssignment = useCallback(async (
    student: QueueStudentRow,
    assignment: QueueAssignmentRow,
  ) => {
    const confirmed = window.confirm(
      `Remove ${student.student_name} from assignment #${assignment.assignment_id}?`,
    );
    if (!confirmed) return;

    const actionKey = assignmentActionKey(student.student_id, assignment.assignment_id);
    setRemovingAssignmentKeys((prev) => new Set([...Array.from(prev), actionKey]));
    setError(null);
    setNotice(null);

    try {
      await assignmentsApi.excuseStudent(assignment.assignment_id, student.student_id, {
        excused: true,
        reason: 'Removed by teacher from consolidated make-up queue.',
      });
      setNotice(`Removed ${student.student_name} from assignment #${assignment.assignment_id}.`);
      await loadQueue();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to remove student from assignment.');
    } finally {
      setRemovingAssignmentKeys((prev) => {
        const next = new Set(prev);
        next.delete(actionKey);
        return next;
      });
    }
  }, [assignmentActionKey, loadQueue]);

  return (
    <OptionalAppShell
      enabled={redesignEnabled}
      role="teacher"
      navItems={teacherV2NavItems('queue')}
      title={queuePurpose === 'assessment' ? 'Assessment Triage' : queuePurpose === 'practice' ? 'Unit Practice Queue' : 'Make-up Queue'}
    >
      {!redesignEnabled && <TeacherNav activeTab="queue" />}
      <div className={redesignEnabled ? 'mx-auto max-w-6xl space-y-4' : 'mx-auto max-w-6xl p-6'}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {queuePurpose === 'assessment' ? 'Assessment Triage' : queuePurpose === 'practice' ? 'Unit Practice Queue' : 'Make-up Queue'}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {queuePurpose === 'assessment'
                ? 'Prioritize saved student evidence before opening an individual assessment review.'
                : queuePurpose === 'practice' ? 'Review independent practice and request revisions without awarding proficiency.' : 'Completed make-ups await your review. Reviewed work stays in student records and is available under Reviewed.'}
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2" aria-label="Queue type">
          <button type="button" className="rounded border px-3 py-2" onClick={() => { setQueuePurpose('practice'); setTriageFilter('all'); }}>Unit practice</button>
          <button
            type="button"
            className={`rounded px-3 py-2 text-sm font-medium ${queuePurpose === 'makeup' ? 'bg-indigo-700 text-white' : 'border border-gray-300 bg-white text-gray-700'}`}
            onClick={() => {
              setQueuePurpose('makeup');
              setTriageFilter('all');
            }}
          >
            Make-up assignments
          </button>
          <button
            type="button"
            className={`rounded px-3 py-2 text-sm font-medium ${queuePurpose === 'assessment' ? 'bg-indigo-700 text-white' : 'border border-gray-300 bg-white text-gray-700'}`}
            onClick={() => {
              setQueuePurpose('assessment');
              setTriageFilter('all');
            }}
          >
            Assessment triage
          </button>
        </div>

        {queuePurpose === 'assessment' && (
          <div className="mt-4 rounded border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            This class view uses only saved Elevate records. No roster or combined class data is sent to AI.
          </div>
        )}

        <div className="mt-4 rounded border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
          Showing {filteredStudents.length} student{filteredStudents.length === 1 ? '' : 's'} | {filteredAssignmentsCount}
          {' '}{queuePurpose === 'assessment' ? 'active assessment' : queuePurpose === 'practice' ? 'unit practice activity' : 'make-up assignment'}{filteredAssignmentsCount === 1 ? '' : 's'}
          {(selectedSectionId !== 'all' || studentQuery.trim() || triageFilter !== 'all' || makeupFilter !== 'all') && (
            <span className="text-gray-500">
              {' '}of {students.length} student{students.length === 1 ? '' : 's'} and {totalAssignments} total assignment
              {totalAssignments === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {queuePurpose === 'assessment' && (
          <p className="mt-4 text-sm text-gray-600">Counts below describe visible student assignments after section, student search, and triage filters. One assignment can appear in more than one category.</p>
        )}
        {queuePurpose === 'assessment' && (
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" aria-label="Assessment triage summary">
            <div className="rounded border border-gray-200 bg-white p-3 text-sm"><span className="font-semibold">{assessmentCounts.written}</span> need written review</div>
            <div className="rounded border border-gray-200 bg-white p-3 text-sm"><span className="font-semibold">{assessmentCounts.missing}</span> missing required evidence</div>
            <div className="rounded border border-gray-200 bg-white p-3 text-sm"><span className="font-semibold">{assessmentCounts.verify}</span> ready to verify</div>
            <div className="rounded border border-gray-200 bg-white p-3 text-sm"><span className="font-semibold">{assessmentCounts.upload}</span> ready for Headrush</div>
          </div>
        )}

        <div className="mt-4 rounded border border-gray-200 bg-white p-4">
          <div className={`grid gap-3 ${queuePurpose === 'assessment' ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              <span className="font-medium text-gray-900">Section visibility</span>
              <select
                aria-label="Section visibility"
                className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                value={sectionVisibility}
                onChange={(event) => {
                  setSectionVisibility(event.target.value as SectionVisibility);
                  setSelectedSectionId('all');
                }}
              >
                <option value="current">Current sections</option>
                <option value="archived">Archived sections</option>
                <option value="all">All sections</option>
              </select>
            </label>
            {queuePurpose === 'assessment' && (
              <label className="flex flex-col gap-1 text-sm text-gray-700">
                <span className="font-medium text-gray-900">Triage status</span>
                <select
                  aria-label="Triage status"
                  className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                  value={triageFilter}
                  onChange={(event) => setTriageFilter(event.target.value as TriageFilter)}
                >
                  <option value="all">All assessment statuses</option>
                  <option value="needs_written_review">Needs written review</option>
                  <option value="missing_evidence">Missing required evidence</option>
                  <option value="follow_up_recommended">Follow-up recommended</option>
                  <option value="retake_requested">Retake approval requested</option>
                  <option value="ready_for_teacher_verification">Ready for teacher verification</option>
                  <option value="ready_for_headrush_upload">Ready for Headrush upload</option>
                  <option value="headrush_upload_confirmed">Headrush upload confirmed</option>
                </select>
              </label>
            )}
            {queuePurpose !== 'assessment' && (
              <label className="flex flex-col gap-1 text-sm text-gray-700">
                <span className="font-medium text-gray-900">Review status</span>
                <select aria-label="Make-up review status" className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900" value={makeupFilter} onChange={(event) => setMakeupFilter(event.target.value as MakeupFilter)}>
                  <option value="action">Needs action</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="all">All make-ups</option>
                </select>
              </label>
            )}
            <PageClassSelector><label className="flex flex-col gap-1 text-sm text-gray-700">
              <span className="font-medium text-gray-900">Section</span>
              <select
                className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                value={selectedSectionId}
                onChange={(event) => setSelectedSectionId(event.target.value)}
              >
                <option value="all">All sections</option>
                {sectionOptions.map((section) => (
                  <option key={`queue-section-${section.id}`} value={String(section.id)}>
                    {section.name}
                  </option>
                ))}
              </select>
            </label></PageClassSelector>

            <label className="flex flex-col gap-1 text-sm text-gray-700">
              <span className="font-medium text-gray-900">Student search</span>
              <input
                type="search"
                className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                placeholder="Search by student name"
                value={studentQuery}
                onChange={(event) => setStudentQuery(event.target.value)}
              />
            </label>
          </div>
        </div>

        {queuePurpose === 'assessment' && visibleAssessmentSummaries.length > 0 && (
          <section className="mt-4 rounded border border-violet-200 bg-violet-50 p-4">
            <h2 className="text-base font-semibold text-violet-950">Class summaries</h2>
            <p className="mt-1 text-xs text-violet-900">Open one assessment and section to review strengths, priority areas, and students needing support.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {visibleAssessmentSummaries.map((assignment) => (
                <Link
                  key={`assessment-summary-${assignment.assignment_id}`}
                  to={`/v2/teacher/assignments/${assignment.assignment_id}/summary`}
                  className="rounded border border-violet-300 bg-white px-3 py-2 text-sm font-semibold text-violet-800"
                  aria-label={`Summarize ${assignment.lesson_plan_title}`}
                >
                  {assignment.lesson_plan_title} · {assignment.section_name}
                </Link>
              ))}
            </div>
          </section>
        )}

        {notice && (
          <div className="mt-4 rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {notice}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {loading ? (
          <p className="mt-4 text-sm text-gray-500">Loading queue...</p>
        ) : filteredStudents.length === 0 ? (
          <p className="mt-4 rounded border border-gray-200 bg-white px-4 py-4 text-sm text-gray-500">
            {students.length === 0
              ? (queuePurpose === 'assessment' ? 'No active assessments right now.' : 'No active make-up queue entries right now.')
              : (queuePurpose === 'assessment' ? 'No assessments match the current filters.' : 'No make-up queue entries match the current filters.')}
          </p>
        ) : queuePurpose === 'assessment' ? (
          <div className="mt-4 space-y-4" aria-label="Assessment rosters">
            {assessmentRosterGroups.map(({ assignment, students: rosterStudents }) => (
              <section key={`assessment-roster-${assignment.assignment_id}`} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
                  <div>
                    <h2 className="font-semibold text-gray-950">{assignment.lesson_plan_title || `Lesson Plan #${assignment.lesson_plan_id}`}</h2>
                    <p className="mt-1 text-xs text-gray-600">
                      {assignment.section_name || `Section #${assignment.section_id}`} · {rosterStudents.length} learner{rosterStudents.length === 1 ? '' : 's'} shown
                    </p>
                  </div>
                  <Link
                    to={`/v2/teacher/assignments/${assignment.assignment_id}/summary`}
                    className="rounded border border-violet-300 bg-white px-3 py-1.5 text-xs font-semibold text-violet-800"
                  >
                    Class summary
                  </Link>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-[900px] w-full border-collapse text-left text-sm">
                    <thead className="bg-white text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th scope="col" className="px-4 py-3 font-semibold">Learner</th>
                        <th scope="col" className="px-4 py-3 font-semibold">Submission</th>
                        <th scope="col" className="px-4 py-3 font-semibold">Evidence review</th>
                        <th scope="col" className="px-4 py-3 font-semibold">Evidence gaps</th>
                        <th scope="col" className="px-4 py-3 font-semibold">Next step</th>
                        <th scope="col" className="px-4 py-3 text-right font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {rosterStudents.map(({ student, assignment: studentAssignment }) => {
                        const triage = studentAssignment.triage;
                        const primaryStatus = triage?.statuses?.[0] || studentAssignment.student_status;
                        const objectivePulse = triage?.objective_item_count
                          ? `${triage.objective_answered_count}/${triage.objective_item_count} answered${triage.objective_score_percent == null ? '' : ` · ${triage.objective_score_percent}%`}`
                          : 'No objective items';
                        const missingCount = (triage?.missing_artifact_count || 0) + (triage?.pending_observation_count || 0);
                        return (
                          <tr key={`assessment-roster-row-${studentAssignment.assignment_id}-${student.student_id}`} className="align-top hover:bg-slate-50">
                            <th scope="row" className="px-4 py-3 font-medium text-gray-950">
                              <Link
                                to={`/v2/teacher/students/${student.student_id}/work?section_id=${studentAssignment.section_id}`}
                                className="text-indigo-700 hover:underline"
                              >
                                {student.student_name}
                              </Link>
                            </th>
                            <td className="px-4 py-3">
                              <span className={`rounded-full px-2 py-0.5 text-xs ${statusPillClass(studentAssignment.student_status)}`}>
                                {formatStatusLabel(studentAssignment.student_status)}
                              </span>
                              <p className="mt-1 text-xs text-gray-600">{objectivePulse}</p>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              <span className="font-medium">{triage?.written_reviewed_count || 0}/{triage?.written_response_count || 0}</span> written reviewed
                              {(triage?.written_needs_revision_count || 0) > 0 && (
                                <p className="mt-1 text-xs font-medium text-amber-800">{triage?.written_needs_revision_count} need revision</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {missingCount > 0 ? (
                                <div className="space-y-1 text-xs">
                                  {(triage?.missing_artifact_count || 0) > 0 && <p>{triage?.missing_artifact_count} artifact{triage?.missing_artifact_count === 1 ? '' : 's'} missing</p>}
                                  {(triage?.pending_observation_count || 0) > 0 && <p>{triage?.pending_observation_count} observation{triage?.pending_observation_count === 1 ? '' : 's'} pending</p>}
                                </div>
                              ) : (
                                <span className="text-xs text-emerald-700">No required gaps</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs text-indigo-800">
                                {triageLabels[primaryStatus] || formatStatusLabel(primaryStatus)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {supportsTeacherReview(studentAssignment.delivery_mode) ? (
                                <button
                                  type="button"
                                  className="rounded bg-indigo-700 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-800"
                                  aria-label={`Quick review ${student.student_name} for assignment ${studentAssignment.assignment_id}`}
                                  onClick={() => openStudentReview(student.student_id, studentAssignment.assignment_id)}
                                >
                                  Quick review
                                </button>
                              ) : (
                                <span className="text-xs text-gray-500">No review page</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {filteredStudents.map((student) => {
              const isRemoving = removingStudentIds.has(student.student_id);
              const assignments = Array.isArray(student.assignments) ? student.assignments : [];
              const totalAssignmentsForStudent = (student as QueueStudentRow & { total_assignment_count?: number }).total_assignment_count
                ?? assignments.length;
              const hasAssignmentRemovalInFlight = assignments.some((assignment) => (
                removingAssignmentKeys.has(assignmentActionKey(student.student_id, assignment.assignment_id))
              ));
              const removeAllLabel = assignments.length === totalAssignmentsForStudent
                ? `Remove from all (${assignments.length})`
                : `Remove shown (${assignments.length})`;
              return (
                <div
                  key={`queue-student-${student.student_id}`}
                  className="rounded-lg border border-gray-200 bg-white p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <Link
                        to={`/v2/teacher/students/${student.student_id}/work${selectedSectionId !== 'all' ? `?section=${selectedSectionId}` : ''}`}
                        className="text-base font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
                      >
                        {student.student_name}
                      </Link>
                      <p className="text-xs text-gray-500">
                        {assignments.length} active assignment{assignments.length === 1 ? '' : 's'}
                        {assignments.length !== totalAssignmentsForStudent
                          ? ` shown of ${totalAssignmentsForStudent}`
                          : ''}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 space-y-2">
                    {assignments.map((assignment) => {
                      const assignmentRemovalKey = assignmentActionKey(student.student_id, assignment.assignment_id);
                      const isRemovingAssignment = removingAssignmentKeys.has(assignmentRemovalKey);
                      return (
                        <div
                          key={`queue-assignment-${student.student_id}-${assignment.assignment_id}`}
                          className="rounded border border-gray-200 bg-gray-50 px-3 py-2"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-medium text-gray-900">
                              {assignment.lesson_plan_title || `Lesson Plan #${assignment.lesson_plan_id}`}
                            </p>
                            <div className="flex items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-xs ${statusPillClass(assignment.student_status)}`}>
                                {formatStatusLabel(assignment.student_status)}
                              </span>
                              {(
                                <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-800">
                                  {assignment.makeup_review?.status === 'approved' ? 'Reviewed'
                                    : assignment.makeup_review?.status === 'needs_revision' ? 'Revision requested'
                                      : assignment.student_status === 'completed' ? 'Awaiting review' : 'Student working'}
                                </span>
                              )}
                              {supportsTeacherReview(assignment.delivery_mode) && (
                                <Link
                                  to={`/v2/teacher/assignments/${assignment.assignment_id}/students/${student.student_id}`}
                                  className="rounded border border-sky-300 bg-white px-2 py-1 text-xs text-sky-700"
                                  aria-label={`View ${student.student_name} results for assignment ${assignment.assignment_id}`}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    navigate(
                                      `/v2/teacher/assignments/${assignment.assignment_id}/students/${student.student_id}`,
                                      {
                                        state: {
                                          queueReturn: `${location.pathname}${location.search}`,
                                          queueScrollY: window.scrollY,
                                        },
                                      },
                                    );
                                  }}
                                >
                                  View results
                                </Link>
                              )}
                            </div>
                          </div>
                          <p className="mt-1 text-xs text-gray-600">
                            Assignment #{assignment.assignment_id}
                            {assignment.run_id ? ` | Run #${assignment.run_id}` : ''}
                            {assignment.run_is_test_mode ? ' (test mode)' : ''}
                            {' | '}
                            {assignment.section_name || `Section #${assignment.section_id}`}
                          </p>
                          <p className="mt-1 text-xs text-gray-600">
                            Assigned {formatDateTime(assignment.assigned_at)}
                            {assignment.due_at ? ` | Due ${formatDateTime(assignment.due_at)}` : ''}
                          </p>
                          {assignment.target_summary && (
                            <p className="mt-1 text-xs text-gray-600">
                              Target: {assignment.target_summary}
                            </p>
                          )}
                          {assignment.why_in_queue && (
                            <p className="mt-1 text-xs text-gray-500">
                              Why in queue: {assignment.why_in_queue}
                            </p>
                          )}
                          {assignment.makeup_review?.note && (
                            <p className="mt-1 text-xs text-amber-800">Teacher feedback: {assignment.makeup_review.note}</p>
                          )}
                          {assignment.student_status === 'completed' && assignment.makeup_review?.status !== 'approved' && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              <button type="button" disabled={reviewingAssignmentKeys.has(assignmentRemovalKey)} onClick={() => { void handleMakeupReview(student, assignment, 'approved'); }} className="rounded border border-emerald-300 bg-white px-2 py-1 text-xs font-semibold text-emerald-800 disabled:opacity-60">Mark reviewed</button>
                              {supportsTeacherReview(assignment.delivery_mode) && <button type="button" disabled={reviewingAssignmentKeys.has(assignmentRemovalKey)} onClick={() => { void handleMakeupReview(student, assignment, 'needs_revision'); }} className="rounded border border-amber-300 bg-white px-2 py-1 text-xs font-semibold text-amber-800 disabled:opacity-60">Send back for revision</button>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <details className="mt-3 rounded border border-gray-200 bg-gray-50">
                    <summary
                      className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-900"
                      aria-label={`Queue actions for ${student.student_name}`}
                    >
                      Queue actions
                    </summary>
                    <div className="space-y-2 border-t border-gray-200 bg-white p-3">
                      <button
                        type="button"
                        className="rounded border border-rose-200 bg-white px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                        onClick={() => {
                          void handleRemoveStudentFromAll(student, assignments);
                        }}
                        disabled={isRemoving || hasAssignmentRemovalInFlight || assignments.length === 0 || assignments.some((assignment) => assignment.student_status === 'completed')}
                      >
                        {isRemoving ? 'Removing...' : removeAllLabel}
                      </button>
                      {assignments.map((assignment) => {
                        const assignmentRemovalKey = assignmentActionKey(student.student_id, assignment.assignment_id);
                        const isRemovingAssignment = removingAssignmentKeys.has(assignmentRemovalKey);
                        return (
                          <div key={`queue-action-${student.student_id}-${assignment.assignment_id}`} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                            <span className="text-gray-600">{assignment.lesson_plan_title || `Assignment #${assignment.assignment_id}`}</span>
                            <button
                              type="button"
                              className="rounded border border-rose-200 bg-white px-2 py-1 text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                              aria-label={`Remove ${student.student_name} from assignment ${assignment.assignment_id}`}
                              onClick={() => {
                                void handleRemoveStudentFromAssignment(student, assignment);
                              }}
                              disabled={isRemoving || isRemovingAssignment || assignment.student_status === 'completed'}
                            >
                              {isRemovingAssignment ? 'Removing...' : 'Remove from this assignment'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </details>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <nav
        aria-label="Queue page controls"
        className="fixed bottom-4 right-4 z-40 flex items-center gap-1 rounded-lg border border-gray-300 bg-white/95 p-1.5 shadow-lg backdrop-blur"
      >
        <button
          type="button"
          className="rounded px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
          onClick={() => void loadQueue()}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <button
          type="button"
          className="rounded px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Go to top of queue"
          title="Go to top"
        >
          ↑ Top
        </button>
        <button
          type="button"
          className="rounded px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
          onClick={() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })}
          aria-label="Go to bottom of queue"
          title="Go to bottom"
        >
          ↓ Bottom
        </button>
      </nav>
    </OptionalAppShell>
  );
};

// The two submenu destinations share this page. Remount on purpose changes so
// navigation and browser Back cannot leave the previous queue's local filters.
export const V2TeacherQueue: React.FC = () => {
  const [params] = useSearchParams();
  return <TeacherQueueContent key={params.get('purpose') || 'makeup'} />;
};

export default V2TeacherQueue;
